import { TextBoxChangeEvent } from '@progress/kendo-react-inputs';
import { useCallback, useMemo } from 'react';
import CircularIconButton from 'src/components/design-system/CircularIconButton';
import TextInput from 'src/components/TextInput';
import useBoolean from 'src/hooks/useBoolean';
import useDebounceCallback from 'src/hooks/useDebounceCallback';
import useInvoiceScanManager from 'src/hooks/useInvoiceScanManager';
import { informationIcon } from 'src/utils/icons';
import { GetColor } from './GetColor';
import MatchPoTable from './MatchPoTable';

export const InputColorMap = {
  primary: '!border-2 !border-primary',
  warning: '!border-2 !border-[#D9AA4F]',
  error: '!border-2 !border-error',
  loading: '!border-2 !border-[#CED4DA]',
};
export const TextColorMap = {
  primary: 'text-primary',
  warning: 'text-[#D9AA4F]',
  error: 'text-error',
  loading: 'text-[#CED4DA]',
};

export default function PoNumberInput() {
  const {
    isPoMatchModalOpen,
    openPoMatchModal,
    closePoMatchModal,
    poNumber,
    setPoNumber,
    getPoValidations,
    ocrPreviewDocument,
    reviewPoValidations,
    reviewPoReqRef,
    initialReviewPoValidation,
    initialLoading,
    validationMessages,
    setReviewPoValidations,
    isReviewPoLoading,
  } = useInvoiceScanManager();

  const { value: isStartTyping, setTrue: startTyping, setFalse: endTyping } = useBoolean();
  const color = useMemo(
    () => GetColor(validationMessages.poValidationMessage.type),
    [validationMessages.poValidationMessage.type]
  );
  const [handleAsyncPoNumberChange] = useDebounceCallback(
    async (attachId: number, value: string) => {
      if (value.length >= 5) {
        await getPoValidations(attachId, value);
      } else {
        setReviewPoValidations((prev) => ({
          ...prev,
          poMatchedSuppliers: [],
          matchStatus: 'PoNumberNotFound',
          matchConfidence: '',
          ocrPoNumber: '',
          poId: null,
        }));
        reviewPoReqRef.current?.cancel();
      }
    }
  );

  const [debounceEndTyping] = useDebounceCallback(endTyping);

  const handleInputChange = useCallback(
    (event: TextBoxChangeEvent) => {
      const newValue = (event.value as string).trim();
      setPoNumber((prev) => ({ ...prev, value: newValue }));
      startTyping();
      handleAsyncPoNumberChange(ocrPreviewDocument?.attachId, newValue);
      debounceEndTyping();
    },
    [
      setPoNumber,
      startTyping,
      handleAsyncPoNumberChange,
      ocrPreviewDocument?.attachId,
      debounceEndTyping,
    ]
  );

  const isShowMatchPo = useMemo(() => {
    let isShowMatchDocument = false;
    const poStatus = reviewPoValidations.matchStatus;
    if (
      poNumber.value &&
      !isReviewPoLoading &&
      (poStatus === 'MultipleMatch' || poStatus === 'InvalidMatch')
    ) {
      isShowMatchDocument = true;
    } else {
      isShowMatchDocument = false;
    }
    return isShowMatchDocument;
  }, [isReviewPoLoading, poNumber.value, reviewPoValidations.matchStatus]);

  const confidencePercentage = useMemo(() => {
    if (initialLoading) return '';
    if (poNumber.value !== (initialReviewPoValidation.ocrPoNumber || '')) return '*Edited';
    return poNumber.matchConfidence ? `*${poNumber.matchConfidence}` : '';
  }, [
    initialLoading,
    initialReviewPoValidation.ocrPoNumber,
    poNumber.matchConfidence,
    poNumber.value,
  ]);

  return (
    <div className="flex flex-col gap-1">
      <div className="flex justify-between">
        <p className="w-full text-sm font-medium">PO Number</p>
        <span className="text-sm text-black font-medium">{confidencePercentage}</span>
      </div>
      <div className="relative">
        <TextInput
          className={` ${InputColorMap[color]} pr-8`}
          onChange={handleInputChange}
          value={initialLoading ? '' : poNumber.value}
        />
        {isShowMatchPo && (
          <div className="absolute right-2 top-1/2 transform -translate-y-1/2 z-50 -m-[2px] cursor-pointer">
            <CircularIconButton
              onClick={openPoMatchModal}
              className="focus-visible:ring-2 focus-visible:ring-black focus-visible:border-black"
            >
              {informationIcon}
            </CircularIconButton>
          </div>
        )}

        <MatchPoTable
          closeModal={closePoMatchModal}
          isModalOpen={isPoMatchModalOpen}
          poNumber={poNumber.value}
        />
      </div>
      <span className={`text-sm ${TextColorMap[color]} min-h-[20px]`}>
        {!initialLoading && !isStartTyping && validationMessages.poValidationMessage.message}
      </span>
    </div>
  );
}
